import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ShopProvider, useShop } from "./context/ShopContext";
import api from "./lib/api";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ComingSoonBanner from "./components/ComingSoonBanner";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Checkout, { OrderSuccess } from "./pages/Checkout";
import Auth from "./pages/Auth";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import StaffSetup from "./pages/StaffSetup";
import PaymentResult from "./pages/PaymentResult";
import Story from "./pages/Story";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const ROUTE_META = {
  "/": ["Velour & Vane — Contemporary Fashion Designed in India", "Premium contemporary fashion for men and women — oversized tees, heavyweight 240 GSM cotton, baggy denim and modern essentials. Designed in India."],
  "/shop": ["Shop the Collection — Velour & Vane", "Browse oversized tees, sweatshirts, baggy jeans, trousers, shirts and accessories. Filter by colour, size, price and fit."],
  "/story": ["Our Story — Velour & Vane", "Velour & Vane brings together considered design, contemporary silhouettes and everyday versatility. Timeless. Refined. Yours."],
};
const NOINDEX_PATHS = ["/checkout", "/auth", "/account", "/admin", "/order-success", "/staff-setup", "/payment"];

function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const [title, desc] = ROUTE_META[pathname] || ROUTE_META["/"];
    document.title = title;
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", desc);
    const r = document.querySelector('meta[name="robots"]');
    if (r) r.setAttribute("content", NOINDEX_PATHS.some((p) => pathname.startsWith(p)) ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
  }, [pathname]);
  return null;
}

function AuthCallback() {
  const { setUser } = useShop();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    api.post("/auth/session", { session_id: sessionId })
      .then(({ data }) => {
        window.history.replaceState(null, "", window.location.pathname);
        setUser(data);
        navigate(["admin", "staff"].includes(data.role) ? "/admin" : "/account", { replace: true, state: { user: data } });
      })
      .catch(() => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/auth", { replace: true });
      });
  }, [navigate, setUser]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center">
      <p className="font-display text-2xl text-vv-stone animate-pulse">Velour &amp; Vane</p>
    </main>
  );
}

function AppShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollManager />
      <RouteMeta />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-success" element={<OrderSuccess />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/account" element={<Account />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/staff-setup" element={<StaffSetup />} />
        <Route path="/payment/result" element={<PaymentResult />} />
        <Route path="/story" element={<Story />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
      <CartDrawer />
      <ComingSoonBanner />
    </>
  );
}

function App() {
  return (
    <ShopProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#111111", color: "#F5F2EA", border: "1px solid rgba(245,242,234,0.15)", borderRadius: 0 } }} />
    </ShopProvider>
  );
}

export default App;
